import { makeVar, useReactiveVar } from '@apollo/client'
import { environment } from './server'

export const BASE_POLLING_INTERVAL = () =>
  window.CONFIG.POLLING_INTERVAL ?? 8000

// we need reactivity here to guarantee that the all the queries' polling intervals change immediately,
// when the value changes, and not just when the component/hook re-renders again, which could be never.
const pollInterval = makeVar<number>(BASE_POLLING_INTERVAL())
export const usePollingInterval = () => useReactiveVar(pollInterval)

let pollingEnabled: boolean = true

// we want to drop the polling rate when the client is idle, but otherwise,
// no other code should be calling this function. give it a big scary name:
export const DANGEROUS_disablePolling = () => {
  pollingEnabled = false
  pollInterval(0)
  console.warn('Disabled polling!')
}

// this is less dangerous but let's still discourage its use:
export const DANGEROUS_enablePolling = () => {
  pollingEnabled = true
  pollInterval(BASE_POLLING_INTERVAL())
  console.warn(`Reset base polling coefficient to: ${BASE_POLLING_INTERVAL()}`)
}

// we're going to set an interval to poll for a new uh polling interval.
// we get this directly from a gcp bucket to avoid involving the backend,
// as "the backend is drowning, and is struggling to serve all requests!"
// is precisely the use case for dropping the app-wide polling interval.
// see services/upload/README.md in hyperion for more on bucket configs.
let interval: any = null

// note:
// - `storage.googleapis.com/BUCKET-NAME` does *not* support CORS
// - `BUCKET-NAME.storage.googleapis.com` *does* support CORS
// more:
// - https://cloud.google.com/storage/docs/cross-origin
// - https://kevinsimper.medium.com/google-cloud-storage-cors-not-working-after-enabling-14693412e404
const CONFIG_JSON_URL = `https://maui-${environment}-config.storage.googleapis.com/pollingInterval.json`

// dont keep trying if it keeps failing
let failCount = 0
const ATTEMPT_COUNT = 6
const GAVE_UP_TEXT = 'Polling interval check keeps failing -- giving up.'

const checkForNewPollingInterval = () => {
  if (failCount >= ATTEMPT_COUNT) {
    clearInterval(interval)
    console.error(GAVE_UP_TEXT)
    return
  }
  fetch(CONFIG_JSON_URL)
    .then((ret) => ret.json())
    .then((nextPollInterval) => {
      // there's nothing to do; bail
      if (nextPollInterval === pollInterval()) {
        console.log(`We have the correct polling interval configured.`)
        return
      }

      // the new value is different but not in a uh good way
      if (typeof nextPollInterval !== 'number')
        throw new TypeError(
          `Invalid polling interval ${nextPollInterval} of type ${typeof nextPollInterval}`
        )

      // do not allow someone to accidentally set seconds instead of milliseconds and ddos ourselves lmao
      if (nextPollInterval > 0 && nextPollInterval < 1000)
        throw new Error(
          `The polling interval config file is set to ${nextPollInterval}. Is the value defined in seconds instead of milliseconds? We're gonna stay at ${pollInterval()}ms... if you meant to do this, you need to remove this guard.`
        )

      // let's just... be loud about this.
      if (nextPollInterval <= 0) console.error('Polling was disabled entirely!')

      // ok, update state!
      console.log(`New polling interval: ${nextPollInterval}`)
      window.CONFIG.POLLING_INTERVAL = nextPollInterval
      if (pollingEnabled) {
        pollInterval(nextPollInterval)
      } else {
        console.log(`We'll use the new interval when the user is back.`)
      }
    })
    .catch((e) => {
      failCount++
      console.error('Error checking for new polling interval:', e)
    })
}

if (import.meta.env.NODE_ENV !== 'test') {
  clearInterval(interval) // dev qol: hmr can sometimes cause these intervals to stack up when running locally. in all other cases, clearInterval(null) is a no-op
  console.log('Initial polling interval:', pollInterval())
  console.log(`Checking for updates at ${CONFIG_JSON_URL}.`)
  interval = setInterval(
    checkForNewPollingInterval,
    window.CONFIG.VERSION_CHECK_INTERVAL_MS || 5 * 60 * 1000
  )
}

//Now do the same as above for our separate cashier polling intervals
const DEFAULT_CASHIER_POLLING_INTERVALS_MS = {
  customer_queue_details: 10000,
  customer_queue_carts: 10000,
  inventory_for_sale: 100000,
  cart_inventory_for_sale: 20000,
  get_one_cart: 10000,
  order_ahead_orders: 20000,
  order_ahead_order_count: 100000,
}

const BASE_CASHIER_POLLING_INTERVALS_MS = () =>
  window.CONFIG.CASHIER_POLLING_INTERVALS_MS ??
  DEFAULT_CASHIER_POLLING_INTERVALS_MS

const cashierPollIntervals = makeVar<Record<string, number>>(
  BASE_CASHIER_POLLING_INTERVALS_MS()
)
export const useCashierPollingIntervals = () =>
  useReactiveVar(cashierPollIntervals)

let cashierInterval: any = null

const CASHIER_CONFIG_JSON_URL = `https://maui-${environment}-config.storage.googleapis.com/cashier_pollingIntervals.json`

let cashierFailCount = 0
const CASHIER_ATTEMPT_COUNT = 6
const CASHIER_GAVE_UP_TEXT =
  'Cashier polling intervals check keeps failing -- giving up.'

const checkForNewCashierPollingIntervals = () => {
  if (cashierFailCount >= CASHIER_ATTEMPT_COUNT) {
    clearInterval(cashierInterval)
    console.error(CASHIER_GAVE_UP_TEXT)
    return
  }
  fetch(CASHIER_CONFIG_JSON_URL)
    .then((ret) => ret.json())
    .then((cashierIntervals) => {
      // there's nothing to do; bail
      if (
        JSON.stringify(cashierIntervals) ===
        JSON.stringify(cashierPollIntervals())
      ) {
        console.log(`We have the correct cashier polling intervals configured.`)
        return
      }
      Object.keys(DEFAULT_CASHIER_POLLING_INTERVALS_MS).forEach((key) => {
        if (!cashierIntervals?.[key]) {
          throw new TypeError(
            `Cashier polling interval for ${key} is not defined in the config file.`
          )
        }
        // the new value is different but not in a uh good way
        if (typeof cashierIntervals[key] !== 'number')
          throw new TypeError(
            `Invalid cashier polling interval for ${key}: ${
              cashierIntervals[key]
            } of type ${typeof cashierIntervals[key]}`
          )

        // do not allow someone to accidentally set seconds instead of milliseconds and ddos ourselves lmao
        if (cashierIntervals[key] > 0 && cashierIntervals[key] < 1000)
          throw new Error(
            `The cashierpolling interval config file is set to ${
              cashierIntervals[key]
            } for ${key}. Is the value defined in seconds instead of milliseconds? We're gonna stay at ${
              cashierPollIntervals()[key]
            }ms... if you meant to do this, you need to remove this guard.`
          )
        // let's just... be loud about this.
        if (cashierIntervals[key] <= 0)
          console.error(`Polling  at ${key} was disabled entirely!`)
      })

      // ok, update state!
      console.log(
        `New cashier polling intervals: ${JSON.stringify(cashierIntervals)}`
      )
      window.CONFIG.CASHIER_POLLING_INTERVALS_MS = cashierIntervals
      if (pollingEnabled) {
        cashierPollIntervals(cashierIntervals)
      } else {
        console.log(
          `We'll use the new cashier intervals when the user is back.`
        )
      }
    })
    .catch((e) => {
      cashierFailCount++
      console.error('Error checking for new cashier polling intervals:', e)
    })
}

if (import.meta.env.NODE_ENV !== 'test') {
  clearInterval(cashierInterval) // dev qol: hmr can sometimes cause these intervals to stack up when running locally. in all other cases, clearInterval(null) is a no-op
  console.log('Initial cashier polling intervals:', cashierPollIntervals())
  console.log(
    `Checking for cashier polling intervals updates at ${CASHIER_CONFIG_JSON_URL}.`
  )

  cashierInterval = setInterval(
    checkForNewCashierPollingIntervals,
    window.CONFIG.VERSION_CHECK_INTERVAL_MS || 5 * 60 * 1000
  )
}
